import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useConnectionContext } from "@/lib/connectionContext";
import { ChevronDownIcon, LayersIcon, PlusIcon } from "@/lib/icons";

interface Props {
  /** When true, render only the icon (sidebar-collapsed mode). */
  collapsed?: boolean;
}

/**
 * Global account-context selector. Sits above the nav in the sidebar.
 * - "All accounts" is the default, auto-includes new connections.
 * - Selecting a subset stores explicit IDs that survive reloads.
 * - When the user has no connections, the switcher prompts them to create one.
 */
export function AccountSwitcher({ collapsed = false }: Props) {
  const ctx = useConnectionContext();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const summary = useMemo(() => {
    if (ctx.status === "loading") return { primary: "Loading…", secondary: "" };
    if (ctx.connections.length === 0) {
      return { primary: "No accounts", secondary: "Connect to start" };
    }
    if (ctx.selection.mode === "all") {
      const total = ctx.connections.length;
      return {
        primary: "All accounts",
        secondary: `${total} ${total === 1 ? "connected" : "connected"}`,
      };
    }
    const ids = ctx.selection.ids;
    if (ids.length === 1) {
      const c = ctx.connections.find((x) => x.id === ids[0]);
      return {
        primary: c?.friendlyName ?? c?.accountSid ?? "Unknown",
        secondary: `1 of ${ctx.connections.length} selected`,
      };
    }
    return {
      primary: `${ids.length} accounts`,
      secondary: `of ${ctx.connections.length} connected`,
    };
  }, [ctx.status, ctx.connections, ctx.selection]);

  if (collapsed) {
    return (
      <div ref={wrapRef} className="relative px-2 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Account context: ${summary.primary}`}
          title={summary.primary}
          className="flex h-9 w-full items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-600 transition hover:border-zinc-300 hover:bg-zinc-50 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300 dark:hover:border-zinc-700 dark:hover:bg-zinc-900 dark:hover:text-zinc-100"
        >
          <LayersIcon size={16} />
        </button>
        {open && <Dropdown collapsed onClose={() => setOpen(false)} />}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative px-3 py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="group flex w-full items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-left transition hover:border-zinc-300 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-zinc-800 dark:bg-zinc-900/60 dark:hover:border-zinc-700 dark:hover:bg-zinc-900"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-500/15 text-brand-600 ring-1 ring-inset ring-brand-500/30 dark:text-brand-300">
          <LayersIcon size={14} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[12.5px] font-medium text-zinc-900 dark:text-zinc-100">
            {summary.primary}
          </span>
          {summary.secondary && (
            <span className="block truncate text-[10.5px] text-zinc-500 dark:text-zinc-500">
              {summary.secondary}
            </span>
          )}
        </span>
        <ChevronDownIcon
          size={12}
          className={
            "shrink-0 text-zinc-400 transition-transform dark:text-zinc-500 " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && <Dropdown onClose={() => setOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Dropdown({ collapsed = false, onClose }: { collapsed?: boolean; onClose: () => void }) {
  const ctx = useConnectionContext();

  if (ctx.connections.length === 0) {
    return (
      <div
        role="menu"
        className={
          "absolute z-30 w-64 overflow-hidden rounded-lg border border-zinc-200 bg-white p-3 shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:backdrop-blur " +
          (collapsed ? "left-full top-2 ml-2" : "left-3 right-3 top-full mt-2 w-auto")
        }
      >
        <p className="text-[12.5px] text-zinc-600 dark:text-zinc-400">
          You haven't connected any Twilio accounts yet.
        </p>
        <Link
          to="/connections/new"
          onClick={onClose}
          className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          <PlusIcon size={12} />
          Connect first account
        </Link>
      </div>
    );
  }

  const isAll = ctx.selection.mode === "all";

  function toggleAll() {
    ctx.setSelection({ mode: "all" });
  }

  function toggleId(id: string) {
    if (ctx.selection.mode === "all") {
      // Switching from "all" to a subset: start with everything except the
      // one being unchecked.
      const ids = ctx.connections.map((c) => c.id).filter((i) => i !== id);
      ctx.setSelection(ids.length === 0 ? { mode: "all" } : { mode: "subset", ids });
      return;
    }
    const current = new Set<string>(ctx.selection.ids);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    if (current.size === 0) {
      ctx.setSelection({ mode: "all" }); // empty → fall back to all
    } else if (current.size === ctx.connections.length) {
      ctx.setSelection({ mode: "all" });
    } else {
      ctx.setSelection({ mode: "subset", ids: Array.from(current) });
    }
  }

  function isChecked(id: string): boolean {
    if (ctx.selection.mode === "all") return true;
    return ctx.selection.ids.includes(id);
  }

  return (
    <div
      role="menu"
      className={
        "absolute z-30 max-h-[80vh] w-72 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:backdrop-blur " +
        (collapsed ? "left-full top-2 ml-2" : "left-3 right-3 top-full mt-2 w-auto")
      }
    >
      <div className="border-b border-zinc-200 px-3 py-2 dark:border-zinc-800">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
          Account context
        </div>
        <div className="mt-0.5 text-[11.5px] text-zinc-500 dark:text-zinc-500">
          Insights and metrics scope to your selection.
        </div>
      </div>

      <div className="max-h-[50vh] overflow-y-auto py-1">
        <CheckboxRow
          checked={isAll}
          onChange={toggleAll}
          label="All accounts"
          sublabel={`${ctx.connections.length} connected`}
          emphasized
        />
        <div className="my-1 mx-3 h-px bg-zinc-200 dark:bg-zinc-800" />
        {ctx.connections.map((c) => (
          <CheckboxRow
            key={c.id}
            checked={isChecked(c.id)}
            onChange={() => toggleId(c.id)}
            label={c.friendlyName ?? c.accountSid}
            sublabel={c.accountSid}
            mono
          />
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2 dark:border-zinc-800">
        <Link
          to="/connections"
          onClick={onClose}
          className="text-[12px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          Manage connections
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="text-[12px] text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
  sublabel,
  emphasized = false,
  mono = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  sublabel?: string;
  emphasized?: boolean;
  mono?: boolean;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 transition hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
      role="menuitemcheckbox"
      aria-checked={checked}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 cursor-pointer accent-brand-500"
      />
      <div className="min-w-0 flex-1">
        <div
          className={
            "truncate text-[12.5px] " +
            (emphasized
              ? "font-semibold text-zinc-900 dark:text-zinc-100"
              : "text-zinc-800 dark:text-zinc-200")
          }
        >
          {label}
        </div>
        {sublabel && (
          <div
            className={
              "truncate text-[10.5px] text-zinc-500 dark:text-zinc-500 " + (mono ? "font-mono" : "")
            }
          >
            {sublabel}
          </div>
        )}
      </div>
    </label>
  );
}
